import Select from "react-select";

import { DateInputFormat, handleRawDate } from "@/common/DateComponents";
import { CustomOption } from "@/common/custom-selectors/CustomOption";
import { MinusIcon, PlusIcon } from "@heroicons/react/20/solid";
import { addDays, isValid, max } from "date-fns";
import ReactDatePicker from "react-datepicker";
import { connect } from "react-redux";
import { dateToString, stringToDate, validateFloat } from "../../common/validation/Validators";

const parseDates = (dates) => {
	if (!dates) return [];

	return dates
		.map((d) => {
			if (!d) return null;
			if (d instanceof Date) return d;
			else return new Date(d);
		})
		.filter((d) => isValid(d));
};

const PensionPlan = ({
	data,
	pensionGroups,
	pensionRules,
	pension,
	updateField,
	disabled: permsDisabled,
	setData,
	contributions,
	optIn,
	optOut,
	setOptIn,
	setOptOut,
	isLast,
	terminationDate,
	percentageOptions,
	forNewEmployee,
	lastPension,
	setReEnroll,
}) => {
	const disabled = permsDisabled || !isLast;
	const classes = !forNewEmployee ? "col-xs-12 col-sm-12 col-lg-8 right" : "col-xs-12";

	if (!pensionRules?.enrolmentBracketCode && false) return null;

	const optedOut = optOut || data?.optOutDate;
	const pensionEnded = pension?.end && pension?.end !== "null";

	return (
		<div className={`${classes} form-section`}>
			<div className="col-xs-6 section-header">
				<h1 className="block-title">Pension Plan</h1>
			</div>
			<div className="col-xs-6">
				{isLast && optedOut && terminationDate !== pension?.end ? (
					<button
						style={{ marginTop: "1.5rem", marginRight: "1rem" }}
						className="btn btn-xs btn-primary pull-right"
						onClick={() => {
							setOptOut(false);
							setReEnroll(true);
							updateField("optOutDate", null);
						}}
					>
						<PlusIcon style={{ height: "1.5em", float: "left" }} />
						<div style={{ float: "left" }}>Re-Enroll</div>
					</button>
				) : null}

				{isLast && pension?.start && !(optOut || data?.optOutDate) && (!pensionRules?.OPT || (optIn || data?.pensionGroup || data?.optInDate)) ? (
					<button
						style={{ marginTop: "1.5rem" }}
						className="btn btn-xs btn-default pull-right"
						onClick={() => {
							setOptOut(!optOut);
							setReEnroll(false);
						}}
					>
						<MinusIcon style={{ height: "1.5em", float: "left" }} />
						<div style={{ float: "left" }}>Opt Out</div>
					</button>
				) : null}

				{pensionRules?.OPT && !(optIn || data?.optInDate) ? (
					<button
						style={{ marginTop: "1.5rem", marginRight: "1rem" }}
						className="btn btn-xs btn-success pull-right"
						onClick={() => setOptIn(!optIn)}
					>
						<PlusIcon style={{ height: "1.5em", float: "left" }} />
						<div style={{ float: "left" }}>Opt In</div>
					</button>
				) : null}

				{/* use === false to ensure that the modal does not show while no pension yet applies */}
				{data?.employeePercentage === false || data?.employerPercentage === false ? (
					<div
						className="badge badge-warning pull-right"
						style={{ marginTop: "1.75rem", marginBottom: "1.75rem", marginRight: "1rem", whiteSpace: "normal"}}
					>
						When using fixed contributions we are unable to confirm you meet the Auto Enrolment minimums.
					</div>
				) : null}
			</div>

			{(!pensionRules?.OPT || optIn || data?.pensionGroup || !!pension?.assessmentDate || forNewEmployee) ? (
				<>
					<div className="col-xs-12 form-container">
						<div className="col-xs-4 pl-0">
							<div className="form-material">
								<label htmlFor="material-text">Pension Group*</label>
								<Select
									value={
										data.pensionGroup
											? pensionGroups.find((item) => item.value == data.pensionGroup) || {
													label: "Deleted Group",
													value: data.pensionGroup,
											  }
											: null
									}
									isDisabled={disabled}
									components={{ Option: CustomOption }}
									onChange={(selected) => {
										if (!selected?.value) {
											setData({});
											return;
										}
										updateField("pensionGroup", selected?.value || null);
									}}
									isClearable
									options={pensionGroups}
								/>
							</div>
						</div>
						{optIn || data?.optInDate || forNewEmployee ? (
							<div className="col-xs-4 pl-0">
								<div className="form-material">
									<label htmlFor="material-text">Opt In Date*</label>
									<ReactDatePicker
										selected={data?.optInDate ? stringToDate(data?.optInDate) : null}
										onChange={(date) => {
											updateField("optInDate", dateToString(date));
										}}
										disabled={data?.optInDate < pension?.start || disabled}
										dateFormat="dd MMM yyyy"
										wrapperClassName="width-full"
										minDate={max(
											parseDates([pension?.start, addDays(new Date(lastPension?.end), 1)]),
										)}
										maxDate={pension?.end ? stringToDate(pension?.end) : undefined}
										customInput={<DateInputFormat />}
										onChangeRaw={(e) =>
											handleRawDate(e.target.value, (date) => {
												updateField("optInDate", dateToString(date));
											})
										}
										strictParsing
									/>
								</div>
							</div>
						) : null}
						{optOut || data?.optOutDate || forNewEmployee ? (
							<div className="col-xs-4 pl-0">
								<div className="form-material">
									<label htmlFor="material-text">Opt Out Date*</label>
									<ReactDatePicker
										selected={data?.optOutDate ? stringToDate(data?.optOutDate) : null}
										onChange={(date) => {
											updateField("optOutDate", dateToString(date));
										}}
										disabled={data?.optOutDate < pension?.start || disabled}
										dateFormat="dd MMM yyyy"
										minDate={max([
											stringToDate(pension?.start),
											stringToDate(data?.optInDate || pension?.start),
										])}
										wrapperClassName="width-full"
										maxDate={pension?.end ? stringToDate(pension?.end) : undefined}
										customInput={<DateInputFormat />}
										onChangeRaw={(e) =>
											handleRawDate(e.target.value, (date) => {
												updateField("optOutDate", dateToString(date));
											})
										}
										strictParsing
									/>
								</div>
							</div>
						) : null}
						<div
							className="col-xs-6 pl-0"
							style={{ height: "4rem" }}
						>
							<div className="form-material">
								<label
									className="css-input switch switch-sm switch-success"
									style={{
										marginTop: "2.5rem",
										cursor: disabled || !data.pensionGroup ? "not-allowed" : "pointer",
									}}
								>
									<input
										checked={data?.customPensionContribution}
										onChange={() =>
											updateField("customPensionContribution", !data?.customPensionContribution)
										}
										type="checkbox"
										disabled={disabled || !data.pensionGroup}
									/>
									<span />
									Custom Contribution
								</label>
							</div>
							<div className="clearfix" />
						</div>
					</div>
					<div className="col-xs-12 pl-0 form-container">
						<div className="col-xs-6 pl-0 pr-0">
							<div className="col-xs-8 pr-0">
								<div className="form-material">
									<label htmlFor="employeeContribution">Employee Contribution</label>
									<input
										className="form-control form-input"
										id="employeeContribution"
										name="employeeContribution"
										value={data?.employeeContribution ?? contributions?.employeeContribution}
										disabled={!data.pensionGroup || !data?.customPensionContribution || disabled}
										style={{ width: "90%", display: "inline-block" }}
										onChange={(e) => {
											updateField(e.target.name, validateFloat(e.target.value));
										}}
										autoComplete="off"
									/>
								</div>
							</div>
							<div className="col-xs-4 pl-0">
								<div className="form-material">
									<Select
										value={percentageOptions?.find(
											({ value }) =>
												value ===
												(data?.employeePercentage ?? contributions?.employeePercentage),
										)}
										isDisabled={disabled || !data?.customPensionContribution || !data.pensionGroup}
										onChange={(selected) => {
											updateField("employeePercentage", selected?.value);
										}}
										options={percentageOptions}
									/>
								</div>
							</div>
						</div>

						<div className="col-xs-6 pl-0 pr-0">
							<div className="col-xs-8 pr-0">
								<div className="form-material">
									<label htmlFor="employerContribution">Employer Contribution</label>
									<input
										className="form-control form-input"
										id="employerContribution"
										name="employerContribution"
										value={data?.employerContribution ?? contributions?.employeeContribution}
										disabled={!data.pensionGroup || !data?.customPensionContribution || disabled}
										style={{ width: "90%", display: "inline-block" }}
										onChange={(e) => {
											updateField(e.target.name, validateFloat(e.target.value));
										}}
										autoComplete="off"
									/>
								</div>
							</div>
							<div className="col-xs-4 pl-0">
								<div className="form-material">
									<Select
										value={percentageOptions?.find(
											({ value }) =>
												value ===
												(data?.employerPercentage ?? contributions?.employerPercentage),
										)}
										isDisabled={disabled || !data?.customPensionContribution || !data.pensionGroup}
										onChange={(selected) => {
											updateField("employerPercentage", selected?.value);
										}}
										options={percentageOptions}
									/>
								</div>
							</div>
						</div>
						<div className="clearfix" />
					</div>
				</>
			) : null}
		</div>
	);
};

const mapStateToProps = (state, { forReview }) => {
	return {
		jurisdiction: state.company?.settings?.jurisdiction,
		terminationDate: state.employee?.agreementInfo?.terminationDate,
		disabled: state?.permissions?.my?.permissions?.people === "Read",
	};
};

const mapDispatchToProps = (dispatch, { notification }) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(PensionPlan);
